import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';

import type { RecentBatchItemsQueryQuery } from '@/apollo/graphql';
import { RECENT_BATCH_ITEMS_QUERY } from '@/gql/queries/batch-action.gql';

export type BatchItemEvent =
  RecentBatchItemsQueryQuery['recentBatchItems'][number];

const POLL_MS = 2000;
const PAGE = 200;
// How long a row counts as "just arrived". The feed's entrance is a mount-time
// animation, but the virtualizer mounts a row whenever it scrolls into view — so
// without an expiry the same arrival list makes rows fly in minutes later, or
// again after scrolling away and back. Long enough to cover the staggered
// entrance (~0.4s + the spring), short enough that no later mount catches it.
export const ARRIVAL_TTL_MS = 1200;

/**
 * Accumulating incremental poll of a batch job's settled items for the live
 * completion feed. Each ~2s poll fetches only items newer than the latest
 * cursor seen (`after`) and APPENDS them — seen items are never re-fetched, so
 * cost is constant regardless of job size. `latestArrivalIds` names the items of
 * the most recent poll for ARRIVAL_TTL_MS — the only ones, and only for as long
 * as, the feed animates. `loadOlder` pages scroll-back history with `before`.
 * Pass `{ paused: true }` to stop polling (e.g. terminal job or nothing to show).
 */
export function useRecentBatchItems(
  jobId: string | null,
  { paused }: { paused: boolean }
) {
  // Ordered ascending internally (by updatedAt,traceId); rendered newest-first.
  const [items, setItems] = useState<BatchItemEvent[]>([]);
  const [latestArrivalIds, setLatestArrivalIds] = useState<string[]>([]);
  const seen = useRef<Set<string>>(new Set());
  const newestCursor = useRef<string | null>(null);
  const oldestCursor = useRef<string | null>(null);
  const loadOlderInFlightRef = useRef(false);
  // Set once a short page proves there is no older history left to fetch.
  const olderExhaustedRef = useRef(false);
  const arrivalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Single owner of the arrival window: announce the ids, then retract them once
  // the entrance has had time to play.
  const markArrivals = useCallback((ids: string[]) => {
    if (arrivalTimer.current) {
      clearTimeout(arrivalTimer.current);
    }
    setLatestArrivalIds(ids);
    arrivalTimer.current = setTimeout(
      () => setLatestArrivalIds([]),
      ARRIVAL_TTL_MS
    );
  }, []);

  useEffect(
    () => () => {
      if (arrivalTimer.current) {
        clearTimeout(arrivalTimer.current);
      }
    },
    []
  );

  const append = useCallback(
    (incoming: readonly BatchItemEvent[], older: boolean) => {
      const fresh = incoming.filter(e => !seen.current.has(e.traceId));
      if (!fresh.length) {
        return;
      }
      for (const e of fresh) seen.current.add(e.traceId);
      setItems(prev => {
        const merged = older ? [...fresh, ...prev] : [...prev, ...fresh];
        // keep ascending by (updatedAt, traceId)
        merged.sort((a, b) =>
          a.updatedAt === b.updatedAt
            ? a.traceId.localeCompare(b.traceId)
            : a.updatedAt.localeCompare(b.updatedAt)
        );
        newestCursor.current =
          merged[merged.length - 1]?.cursor ?? newestCursor.current;
        oldestCursor.current = merged[0]?.cursor ?? oldestCursor.current;
        return merged;
      });
      if (!older) {
        markArrivals(fresh.map(e => e.traceId));
      }
    },
    [markArrivals]
  );

  // Initial page (after: null) fetches the most-recent settled items on mount.
  // Apollo's own pollInterval CANNOT carry a moving `after`, so polling is an
  // explicit setInterval (below) that advances the cursor each tick via
  // fetchMore — this is the single, deliberate approach (not pollInterval).
  // NOTE: `skip` is gated ONLY on jobId, NOT on `paused`. `paused` stops the
  // forward POLL (below) — there are no new items on a terminal job — but the
  // initial page must ALWAYS load, otherwise reopening a COMPLETED batch after a
  // reload (job already terminal on mount → paused=true) skips this query and
  // the feed shows nothing but whatever a race let through ("only a few rows").
  const reset = useCallback(() => {
    if (arrivalTimer.current) {
      clearTimeout(arrivalTimer.current);
    }
    setItems([]);
    setLatestArrivalIds([]);
    seen.current = new Set();
    newestCursor.current = null;
    oldestCursor.current = null;
    olderExhaustedRef.current = false;
  }, []);

  const { fetchMore, loading, refetch } = useQuery(RECENT_BATCH_ITEMS_QUERY, {
    variables: { jobId: jobId ?? '', limit: PAGE, after: null, before: null },
    skip: !jobId,
    // Cached first page paints immediately when a job is re-opened; `append`
    // dedupes by traceId, so the network answer that follows adds no rows twice.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => append(data.recentBatchItems ?? [], false)
  });

  // FIX 1: Reset accumulator when jobId changes (e.g. retry → new job).
  useEffect(() => {
    reset();
  }, [jobId, reset]);

  // Moving-cursor forward poll: each tick fetches only items newer than the
  // newest cursor seen. Explicit setInterval because pollInterval would re-run
  // the initial (after: null) query and re-fetch from the top every tick.
  useEffect(() => {
    if (paused || !jobId) {
      return;
    }
    const id = setInterval(() => {
      fetchMore({
        variables: {
          jobId,
          limit: PAGE,
          after: newestCursor.current,
          before: null
        }
      })
        .then(res => append(res.data?.recentBatchItems ?? [], false))
        .catch(() => {}); // FIX 3: guard transient network errors
    }, POLL_MS);
    return () => clearInterval(id);
  }, [paused, jobId, fetchMore, append]);

  // FIX 2: One final catch-up fetch when job goes terminal.
  const wasPolling = useRef(false);
  useEffect(() => {
    if (paused && wasPolling.current && jobId) {
      // final catch-up flush of the tail
      fetchMore({
        variables: {
          jobId,
          limit: PAGE,
          after: newestCursor.current,
          before: null
        }
      })
        .then(res => append(res.data?.recentBatchItems ?? [], false))
        .catch(() => {}); // FIX 3: guard transient network errors
    }
    wasPolling.current = !paused && !!jobId;
  }, [paused, jobId, fetchMore, append]);

  const loadOlder = useCallback(() => {
    if (
      !(jobId && oldestCursor.current) ||
      loadOlderInFlightRef.current ||
      olderExhaustedRef.current
    ) {
      return;
    }
    loadOlderInFlightRef.current = true;
    fetchMore({
      variables: {
        jobId,
        limit: PAGE,
        before: oldestCursor.current,
        after: null
      }
    })
      .then(res => {
        const page = res.data?.recentBatchItems ?? [];
        // A short page means the keyset reached the oldest item; without this we
        // would refire on every scroll event at the bottom, forever.
        if (page.length < PAGE) {
          olderExhaustedRef.current = true;
        }
        append(page, true);
      })
      .finally(() => {
        loadOlderInFlightRef.current = false;
      });
  }, [jobId, fetchMore, append]);

  // A single-item retry rewrites a row IN PLACE, and `append` keeps the first
  // version of every traceId it has seen — so the row can only pick up its new
  // status by dropping the accumulator and reading the head page again.
  const refresh = useCallback(async () => {
    if (!jobId) {
      return;
    }
    reset();
    const res = await refetch({
      jobId,
      limit: PAGE,
      after: null,
      before: null
    });
    append(res.data?.recentBatchItems ?? [], true);
  }, [jobId, refetch, append, reset]);

  const newestFirst = useMemo(() => [...items].reverse(), [items]);
  // `loading` also flips during the forward poll, but the feed only shows
  // skeletons while it has nothing to show, so a refetch never blanks the list.
  return { items: newestFirst, latestArrivalIds, loadOlder, loading, refresh };
}
